import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { InputTextComponent } from '../input-text/input-text.component';

@Component({
  selector: 'app-secure-url-input',
  templateUrl: './secure-url-input.component.html',
  styleUrls: ['./secure-url-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SecureUrlInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SecureUrlInputComponent),
      multi: true,
    },
  ],
})
export class SecureUrlInputComponent
  implements ControlValueAccessor, Validator, OnInit, AfterViewInit, OnDestroy
{
  /** The composed base primitive; its `bareInput` is the native input. */
  @ViewChild(InputTextComponent) private urlInput?: InputTextComponent;

  // Input properties
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = 'Enter URL';
  @Input() maxLength: number = 500;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() autocomplete: string = 'off';
  @Input() cssClass: string = 'form-control';

  // Output events
  @Output() changeEvent = new EventEmitter<string>();
  @Output() blurEvent = new EventEmitter<FocusEvent>();
  @Output() focusChange = new EventEmitter<FocusEvent>();

  // Internal state
  private readonly HTTPS_PREFIX = 'https://';
  private destroy$ = new Subject<void>();
  private lastEmittedValue: string = '';

  // ControlValueAccessor properties
  private onChange = (value: string) => {};
  private onTouched = () => {};

  // Component state
  value: string = this.HTTPS_PREFIX;
  isInvalid: boolean = false;
  errorMessage: string = '';
  private _isFocused: boolean = false; // Track focus state

  // Getter to determine if placeholder should be shown
  get shouldShowPlaceholder(): boolean {
    // Only show placeholder if unfocused, value is just https://, and a custom placeholder is provided
    return (
      !this._isFocused &&
      this.value === this.HTTPS_PREFIX &&
      this.placeholder &&
      this.placeholder !== 'Enter URL'
    );
  }

  // URL validation regex (after https:// prefix)
  private readonly URL_REGEX =
    /^(?!https?:\/\/)(?!\/)(?:(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,}|xn--[a-z0-9-]{2,})|(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3})(?::\d{2,5})?(?:\/[^\s?#]*)?(?:\?[^\s#]*)?(?:#[^\s]*)?$/i;

  ngOnInit(): void {
    if (!this.id) {
      this.id =
        'secure-url-input-' + Math.random().toString(36).substring(2, 11);
    }
  }

  ngAfterViewInit(): void {
    const input = this.nativeInput;
    if (input) {
      // The base primitive exposes no paste output; keep the widget's
      // protocol-normalising paste handling on the composed native input.
      input.addEventListener('paste', this.handlePaste);
    }
    this.syncInputAttributes();
  }

  ngOnDestroy(): void {
    this.nativeInput?.removeEventListener('paste', this.handlePaste);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get nativeInput(): HTMLInputElement | null {
    return this.urlInput?.bareInput?.nativeElement ?? null;
  }

  private handlePaste = (event: ClipboardEvent): void => this.onPaste(event);

  /**
   * Reapply the attributes the raw input used to carry that the base
   * primitive doesn't render itself (name, required, aria-invalid,
   * aria-describedby).
   */
  private syncInputAttributes(): void {
    const input = this.nativeInput;
    if (!input) {
      return;
    }
    if (this.name) {
      input.setAttribute('name', this.name);
    }
    if (this.required) {
      input.setAttribute('required', '');
    } else {
      input.removeAttribute('required');
    }
    input.setAttribute('aria-invalid', String(this.isInvalid));
    if (this.isInvalid) {
      input.setAttribute('aria-describedby', this.id + '-error');
    } else {
      input.removeAttribute('aria-describedby');
    }
  }

  /**
   * Handle input events - only update internal state, don't emit changes
   */
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;

    // Ensure HTTPS prefix is always present
    if (!currentValue.startsWith(this.HTTPS_PREFIX)) {
      this.value = this.HTTPS_PREFIX;
      input.value = this.HTTPS_PREFIX;
      this.setCursorPosition(input, this.HTTPS_PREFIX.length);
    } else {
      this.value = currentValue;
    }

    // Only validate, don't emit changes on every keystroke
    this.validateUrl();
  }

  /**
   * Handle paste events - only update internal state, don't emit changes
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const pastedText = event.clipboardData?.getData('text') || '';

    // Process the pasted URL
    const processedUrl = this.processPastedUrl(pastedText);

    // Update values
    this.value = processedUrl;
    input.value = processedUrl;

    // Only validate, don't emit changes on paste - wait for blur
    this.validateUrl();
  }

  /**
   * Handle focus events
   */
  onFocus(event: FocusEvent): void {
    this.focusChange.emit(event);
    this._isFocused = true; // Set focus state

    // If the field is empty (showing placeholder), prepare for input
    const input = event.target as HTMLInputElement;
    if (this.value === this.HTTPS_PREFIX) {
      setTimeout(() => {
        this.setCursorPosition(input, this.HTTPS_PREFIX.length);
      }, 0);
    }
  }

  /**
   * Handle blur events - emit changes when user finishes typing
   */
  onBlur(event: FocusEvent): void {
    this._isFocused = false; // Clear focus state
    this.onTouched();

    // Extract meaningful value (without https:// prefix)
    const meaningfulValue = this.extractMeaningfulValue(this.value);

    // If only https:// prefix remains, emit empty string
    // Otherwise emit the complete URL
    const payloadValue = meaningfulValue.trim() === '' ? '' : this.value;

    // Only emit if the value has actually changed
    if (payloadValue !== this.lastEmittedValue) {
      this.lastEmittedValue = payloadValue;
      this.onChange(payloadValue);
      this.changeEvent.emit(payloadValue);
    }

    this.blurEvent.emit(event);
  }

  /**
   * Process pasted URL to handle different protocols
   */
  private processPastedUrl(pastedUrl: string): string {
    const trimmedUrl = pastedUrl.trim();

    // If already has https://, use as is
    if (trimmedUrl.startsWith(this.HTTPS_PREFIX)) {
      return trimmedUrl;
    }

    // If has http://, replace with https://
    if (trimmedUrl.startsWith('http://')) {
      return this.HTTPS_PREFIX + trimmedUrl.substring('http://'.length);
    }

    // If no protocol, add https://
    if (trimmedUrl && !trimmedUrl.includes('://')) {
      return this.HTTPS_PREFIX + trimmedUrl;
    }

    // Default to https://
    return this.HTTPS_PREFIX;
  }

  /**
   * Extract meaningful value (without https:// prefix)
   */
  private extractMeaningfulValue(fullValue: string): string {
    if (fullValue === this.HTTPS_PREFIX) {
      return ''; // Empty string for just the prefix
    }
    return fullValue.startsWith(this.HTTPS_PREFIX)
      ? fullValue.substring(this.HTTPS_PREFIX.length)
      : fullValue;
  }

  /**
   * Set cursor position
   */
  private setCursorPosition(input: HTMLInputElement, position: number): void {
    setTimeout(() => {
      input.setSelectionRange(position, position);
    }, 0);
  }

  /**
   * Validate the URL format
   */
  private validateUrl(): void {
    const urlWithoutPrefix = this.value.substring(this.HTTPS_PREFIX.length);

    // Empty URL (only https://) is considered valid
    if (!urlWithoutPrefix.trim()) {
      this.isInvalid = false;
      this.errorMessage = '';
      this.syncInputAttributes();
      return;
    }

    // Validate URL format
    const isValid = this.URL_REGEX.test(urlWithoutPrefix);
    this.isInvalid = !isValid;
    this.errorMessage = isValid ? '' : 'The URL is invalid';
    this.syncInputAttributes();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (value !== undefined && value !== null) {
      // If the value doesn't start with https://, add it
      const fullValue = value.startsWith(this.HTTPS_PREFIX)
        ? value
        : this.HTTPS_PREFIX + value;
      this.value = fullValue;

      // Extract meaningful value for tracking
      const meaningfulValue = this.extractMeaningfulValue(fullValue);
      this.lastEmittedValue = meaningfulValue.trim() === '' ? '' : fullValue;
    } else {
      this.value = this.HTTPS_PREFIX;
      this.lastEmittedValue = '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';

    // If the field is required and empty, return required error
    if (this.required && !value.trim()) {
      return { required: true };
    }

    // If not required and empty, it's valid
    if (!this.required && !value.trim()) {
      return null;
    }

    // Validate URL format when there's a value
    if (
      value.trim() &&
      !this.URL_REGEX.test(value.substring(this.HTTPS_PREFIX.length))
    ) {
      return { invalidUrl: true };
    }

    return null;
  }
}
